import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { loadCompanyPrefs } from "@/data/companyPrefs";
import {
  parseMaterialsOwnership,
  type ClientOfferTextOverrides,
  type ClientOfferClientDetails,
  type EstimateLine,
  type MaterialsOwnership,
  type OfferRow,
  type Room,
  type ScopeId,
  type Step3CleanupOwner,
  type Step3OfferExtras,
  type Step3PriceVariant,
  type Step3ProjectMilestone,
  type Step3ScheduleStage,
  type VatMode,
  type WizardInput,
} from "@/types/mvpEstimate";
import {
  defaultCheckedAssumptionTexts,
  DEFAULT_PAYMENT_TEXT,
  DEFAULT_STEP3_SECTION_INCLUDES,
  DEFAULT_WARRANTY_TEXT,
  normalizeStep3SectionIncludes,
  savedAssumptionDataIsStale,
} from "@/components/mvp/step3OfferTerms";
import type { Step3SectionIncludes } from "@/components/mvp/step3OfferTerms";
import { isSectionHeader, parseWorkTypes, WORK_TYPE_LABELS } from "@/types/mvpEstimate";
import { normalizeLinesVatRates } from "@/utils/mvpEstimateMath";
import {
  computeScopeIdsFromSelectedItems,
  inferSelectedItemsFromScopeIds,
  normalizeSelectionToPackages,
} from "@/wizard/mvpWizardScopeCatalog";
import type { TemplateType } from "@/components/offer/offerDocumentTypes";
import {
  DEFAULT_TEMPLATE_ID,
  OFFER_TEMPLATES,
  type OfferTemplateId,
} from "@/types/offerTemplates";

const DEBOUNCE_MS = 1500;

function parseVatMode(raw: unknown): VatMode {
  return raw === "8" || raw === "23" || raw === "none" ? raw : "none";
}

export type MvpWorkshopStep = 1 | 2 | 3 | 4;
export type MvpEntryMode = "packages" | "manual";

function parseTemplateType(raw: unknown): TemplateType {
  return raw === "quick" || raw === "explained" || raw === "premium" ? raw : "quick";
}

function parseMvpEntryMode(raw: unknown): MvpEntryMode {
  return raw === "manual" ? "manual" : "packages";
}

const VALID_TEMPLATE_IDS = new Set(OFFER_TEMPLATES.map((t) => t.id));

function parseTemplateId(raw: unknown): OfferTemplateId {
  return typeof raw === "string" && VALID_TEMPLATE_IDS.has(raw as OfferTemplateId)
    ? (raw as OfferTemplateId)
    : DEFAULT_TEMPLATE_ID;
}

export type MvpBundle = {
  wizard: WizardInput;
  lines: OfferRow[];
  showEstimate: boolean;
  vatMode: VatMode;
  /** Ostatni aktywny krok kreatora (1–4) — przywracany przy otwarciu szkicu. */
  currentStep?: MvpWorkshopStep;
  draftLabel?: string;
  draftUpdatedAt?: number;
  /** Opcjonalne teksty w podglądzie oferty (MVP). */
  offerTexts?: ClientOfferTextOverrides;
  /** Dane klienta do nagłówka oferty (MVP). */
  clientDetails?: ClientOfferClientDetails;
  /** Założenia i warunki — zaznaczone predefiniowane + własne wpisy (MVP). */
  offerAssumptions?: string[];
  /** Kto dostarcza materiały (krok 3 — wymagane przed krokiem 4). */
  materialsOwnership?: MaterialsOwnership;
  /** Uwagi do materiałów (opcjonalnie). */
  materialsNotes?: string;
  /** Typ dokumentu oferty — kolejność i widoczność sekcji renderowanych dla klienta. */
  templateType?: TemplateType;
  entryMode?: MvpEntryMode;
  /** Szablon wizualny oferty (krok 4). */
  templateId?: OfferTemplateId;
  paymentTerms?: string;
  /** Tekst gwarancji w ofercie (krok 3). */
  warrantyType?: string;
  /** Wyłączenia odpowiedzialności (krok 3). */
  exclusions?: string[];
  /** Które sekcje warunków trafiają do oferty (krok 3). */
  step3SectionIncludes?: Step3SectionIncludes;
  step3Extras?: Step3OfferExtras;
  /** @deprecated Legacy — odczyt przy wczytywaniu starych szkiców. */
  warrantyNotes?: string;
};

export type MvpDraftRecord = {
  id: string;
  name: string;
  updatedAt: number;
  bundle: MvpBundle;
};

export type MvpSaveStatus = "idle" | "saving" | "saved";

export type MvpDraftSummary = {
  id: string;
  name: string;
  updatedAt: number;
  projectId?: string | null;
};

export type ListMvpDraftOptions = {
  /** null = tylko szkice bez projektu; uuid = przypisane do projektu; undefined = wszystkie */
  projectId?: string | null;
};

export function defaultWizardInput(): WizardInput {
  const rooms: Room[] = [
    {
      id: crypto.randomUUID(),
      name: "Salon / Pokój dzienny",
      areaM2: 0,
      wallHeight: 2.6,
      selectedScopeItemIds: [],
      scopeIds: [],
    },
  ];
  return {
    step: 0,
    workType: ["malowanie"],
    rooms,
    standard: "standard",
    region: "regionalne",
  };
}

// ─── Schema normalization (kept for tests & backward compat) ────────────────

/**
 * Zapis lokalny / migracja — `roomLabel` → `projectDescription`; stary JSON mógł mieć
 * płaskie `areaM2` zamiast `rooms` (obsługa w `normalizeWizardScopes`).
 */
type WizardInputLoad = Partial<WizardInput> & {
  roomLabel?: string;
  workType?: unknown;
  /** Legacy — tylko odczyt przy wczytywaniu, mapowane na `rooms`. */
  areaM2?: number;
  /** Legacy przy `JSON.parse` — globalne zakresy przed migracją per pokój (Pass 4). */
  scopeIds?: ScopeId[];
  /** Legacy przy `JSON.parse` — pakiety globalne przed migracją per pokój (Pass 4). */
  selectedScopeItemIds?: string[];
};

/**
 * Jedno pomieszczenie ze starego zapisu → pełny `Room` (pola zakresu domyślnie puste).
 */
function normalizeRoomScopesFromLoad(raw: unknown, fallbackIndex: number): Room {
  const r = raw as Partial<Room> & Record<string, unknown>;
  const id =
    typeof r.id === "string" && r.id.trim() ? r.id : crypto.randomUUID();
  const name =
    typeof r.name === "string" ? r.name : `Pomieszczenie ${fallbackIndex + 1}`;
  const areaM2 =
    typeof r.areaM2 === "number" && !Number.isNaN(r.areaM2) ? Math.max(0, r.areaM2) : 0;
  const wallHeight =
    typeof r.wallHeight === "number" && !Number.isNaN(r.wallHeight) ? r.wallHeight : 2.6;

  let selectedScopeItemIds: string[] = [];
  if (Array.isArray(r.selectedScopeItemIds) && r.selectedScopeItemIds.length > 0) {
    selectedScopeItemIds = normalizeSelectionToPackages(r.selectedScopeItemIds);
  } else if (Array.isArray(r.scopeIds) && r.scopeIds.length > 0) {
    selectedScopeItemIds = normalizeSelectionToPackages(
      inferSelectedItemsFromScopeIds(r.scopeIds as ScopeId[]),
    );
  }
  const scopeIds = computeScopeIdsFromSelectedItems(selectedScopeItemIds);

  return {
    id,
    name,
    areaM2,
    wallHeight,
    selectedScopeItemIds,
    scopeIds,
  };
}

/** Eksportowane pod unit testy migracji (Pass 3). */
export function normalizeWizardScopes(wizard: WizardInputLoad): WizardInput {
  const def = defaultWizardInput();
  let selected = wizard.selectedScopeItemIds;
  if (!Array.isArray(selected) || selected.length === 0) {
    selected =
      wizard.scopeIds?.length > 0 ? inferSelectedItemsFromScopeIds(wizard.scopeIds) : [];
  }
  selected = normalizeSelectionToPackages(selected);
  const scopeIds = computeScopeIdsFromSelectedItems(selected);
  /** Kreator ma 3 kroki (0–2); stare zapisy 2–4 → krok „zakres". */
  const rawStep = typeof wizard.step === "number" && !Number.isNaN(wizard.step) ? wizard.step : 0;
  const step = rawStep <= 1 ? Math.max(0, rawStep) : 2;
  const projectNotes =
    typeof wizard.projectNotes === "string" ? wizard.projectNotes : undefined;
  const workType = parseWorkTypes(wizard.workType);
  const legacyRoom =
    typeof wizard.roomLabel === "string" && wizard.roomLabel.trim()
      ? wizard.roomLabel.trim()
      : undefined;
  const projectDescription =
    typeof wizard.projectDescription === "string" && wizard.projectDescription.trim()
      ? wizard.projectDescription.trim()
      : legacyRoom;

  let rooms: Room[];
  const rawRooms = wizard.rooms;
  if (Array.isArray(rawRooms) && rawRooms.length > 0) {
    rooms = rawRooms.map((raw, i) => normalizeRoomScopesFromLoad(raw, i));
  } else if (typeof wizard.areaM2 === "number" && !Number.isNaN(wizard.areaM2)) {
    rooms = [
      normalizeRoomScopesFromLoad(
        {
          id: crypto.randomUUID(),
          name: "Pomieszczenie 1",
          areaM2: wizard.areaM2,
          wallHeight: 2.6,
        },
        0,
      ),
    ];
  } else {
    rooms = def.rooms;
  }

  /** Legacy Pass 4: globalny zakres w zapisie, brak zakresów per pokój → kopiuj na pokój 0. */
  const noRoomHasScopes = rooms.every((r) => r.scopeIds.length === 0);
  if (scopeIds.length > 0 && noRoomHasScopes && rooms.length > 0) {
    rooms = rooms.map((r, i) =>
      i === 0
        ? {
            ...r,
            selectedScopeItemIds: [...selected],
            scopeIds: [...scopeIds],
          }
        : { ...r, selectedScopeItemIds: [], scopeIds: [] },
    );
  }

  return {
    step,
    workType,
    projectDescription,
    rooms,
    standard: wizard.standard ?? def.standard,
    region: wizard.region ?? def.region,
    projectNotes,
  };
}

// ─── Bundle helpers ──────────────────────────────────────────────────────────

export function parseMvpCurrentStep(raw: unknown): MvpWorkshopStep | null {
  const n = typeof raw === "number" && !Number.isNaN(raw) ? raw : null;
  if (n === 1 || n === 2 || n === 3 || n === 4) return n;
  return null;
}

/** Czy szkic ma jakąkolwiek treść poza pustym stanem startowym (odblokowuje nawigację kroków). */
export function hasMvpDraftProgress(bundle: MvpBundle): boolean {
  if (bundle.showEstimate) return true;
  if (bundle.lines.some((row) => !isSectionHeader(row))) return true;
  const cd = bundle.clientDetails;
  if (cd && ((cd.name ?? "").trim() || (cd.address ?? "").trim())) return true;
  if (bundle.offerAssumptions && bundle.offerAssumptions.length > 0) return true;
  if (bundle.materialsOwnership) return true;
  if ((bundle.materialsNotes ?? "").trim()) return true;
  if ((bundle.warrantyType ?? "").trim() && bundle.warrantyType !== "hidden") return true;
  if (bundle.exclusions && bundle.exclusions.length > 0) return true;
  if ((bundle.warrantyNotes ?? "").trim()) return true;
  const w = bundle.wizard;
  if (typeof w.projectDescription === "string" && w.projectDescription.trim()) return true;
  if (
    w.rooms.some(
      (r) =>
        r.areaM2 > 0 ||
        r.scopeIds.length > 0 ||
        (r.selectedScopeItemIds?.length ?? 0) > 0,
    )
  ) {
    return true;
  }
  return false;
}

export function inferMvpWorkshopStepFromDraft(bundle: MvpBundle): MvpWorkshopStep {
  const clientName = bundle.clientDetails?.name?.trim() ?? "";
  const investmentAddress = bundle.clientDetails?.address?.trim() ?? "";
  if (clientName || investmentAddress) return 3;
  if (bundle.lines.some((row) => !isSectionHeader(row))) return 2;
  return 1;
}

function parseMvpBundle(raw: unknown): MvpBundle | null {
  if (!raw || typeof raw !== "object") return null;
  try {
    const parsed = raw as Partial<MvpBundle>;
    if (!parsed.wizard || !Array.isArray(parsed.lines)) return null;
    const wizard = normalizeWizardScopes({
      ...defaultWizardInput(),
      ...parsed.wizard,
    } as WizardInputLoad);
    const vatMode = parseVatMode((parsed as Partial<MvpBundle>).vatMode);
    const rowsRaw = parsed.lines as unknown[];
    const lines = rowsRaw.map((row, rowIndex): OfferRow => {
      if (row && typeof row === "object" && (row as { type?: unknown }).type === "section") {
        const section = row as Partial<{ id: string; title: string }>;
        const id = typeof section.id === "string" && section.id.trim() ? section.id : crypto.randomUUID();
        const title =
          typeof section.title === "string" && section.title.trim()
            ? section.title
            : `Sekcja ${rowIndex + 1}`;
        return { type: "section", id, title };
      }
      const normalized = normalizeLinesVatRates([row as EstimateLine], vatMode);
      return normalized[0];
    });
    const showEstimate = Boolean(parsed.showEstimate ?? lines.some((row) => !isSectionHeader(row)));
    const draftLabel =
      typeof parsed.draftLabel === "string" ? parsed.draftLabel : undefined;
    const draftUpdatedAt =
      typeof parsed.draftUpdatedAt === "number" ? parsed.draftUpdatedAt : undefined;
    const offerTexts =
      parsed.offerTexts && typeof parsed.offerTexts === "object"
        ? (parsed.offerTexts as ClientOfferTextOverrides)
        : undefined;
    const clientDetailsRaw = (parsed as Partial<MvpBundle>).clientDetails as unknown;
    const clientDetails =
      clientDetailsRaw && typeof clientDetailsRaw === "object"
        ? (clientDetailsRaw as ClientOfferClientDetails)
        : undefined;
    const templateType = parseTemplateType((parsed as Partial<MvpBundle>).templateType);
    const entryMode = parseMvpEntryMode((parsed as Partial<MvpBundle>).entryMode);
    const templateId = parseTemplateId((parsed as Partial<MvpBundle>).templateId);
    const toStringOrUndefined = (value: unknown): string | undefined =>
      typeof value === "string" ? value : undefined;
    const toStringListOrUndefined = (value: unknown): string[] | undefined =>
      Array.isArray(value)
        ? value.filter((x): x is string => typeof x === "string" && x.trim().length > 0)
        : undefined;
    const rawAssumptions = (parsed as Partial<MvpBundle>).offerAssumptions;
    const offerAssumptionsRaw = Array.isArray(rawAssumptions)
      ? rawAssumptions.filter((x): x is string => typeof x === "string" && x.trim().length > 0)
      : undefined;
    const exclusionsRaw = toStringListOrUndefined((parsed as Partial<MvpBundle>).exclusions);
    const assumptionsStale = savedAssumptionDataIsStale(offerAssumptionsRaw, exclusionsRaw);
    const offerAssumptions = assumptionsStale
      ? defaultCheckedAssumptionTexts()
      : offerAssumptionsRaw;
    const currentStep = parseMvpCurrentStep((parsed as Partial<MvpBundle>).currentStep);
    const materialsOwnership = parseMaterialsOwnership(
      (parsed as Partial<MvpBundle>).materialsOwnership,
    );
    const materialsNotes =
      typeof (parsed as Partial<MvpBundle>).materialsNotes === "string"
        ? (parsed as Partial<MvpBundle>).materialsNotes
        : undefined;
    const rawWarrantyType = (parsed as Partial<MvpBundle>).warrantyType;
    const warrantyNotes =
      typeof (parsed as Partial<MvpBundle>).warrantyNotes === "string"
        ? (parsed as Partial<MvpBundle>).warrantyNotes
        : undefined;
    let warrantyType: string | undefined;
    if (typeof rawWarrantyType === "string") {
      const trimmed = rawWarrantyType.trim();
      if (trimmed === "hidden") {
        warrantyType = undefined;
      } else if (trimmed === "standard") {
        warrantyType = DEFAULT_WARRANTY_TEXT;
      } else if (trimmed === "custom") {
        const legacyMonths = warrantyNotes?.match(/^(\d+)\s*mies/i);
        warrantyType = legacyMonths
          ? `Gwarancja: ${legacyMonths[1]} miesięcy od dnia odbioru prac.`
          : DEFAULT_WARRANTY_TEXT;
      } else if (trimmed.length > 0) {
        warrantyType = trimmed;
      }
    }
    const exclusions = assumptionsStale ? undefined : exclusionsRaw;
    const rawIncludes = (parsed as Partial<MvpBundle>).step3SectionIncludes;
    const step3SectionIncludes: Step3SectionIncludes | undefined =
      rawIncludes && typeof rawIncludes === "object"
        ? normalizeStep3SectionIncludes(
            rawIncludes as Partial<Step3SectionIncludes>,
            templateType
          )
        : undefined;
    const step3Extras = parseStep3Extras((parsed as Partial<MvpBundle>).step3Extras);
    return {
      wizard,
      lines,
      showEstimate,
      vatMode,
      ...(currentStep != null ? { currentStep } : {}),
      draftLabel,
      draftUpdatedAt,
      offerTexts,
      clientDetails,
      templateType,
      entryMode,
      templateId,
      ...(materialsOwnership ? { materialsOwnership } : {}),
      ...(materialsNotes !== undefined ? { materialsNotes } : {}),
      paymentTerms: toStringOrUndefined((parsed as Partial<MvpBundle>).paymentTerms),
      ...(Array.isArray(rawAssumptions) || assumptionsStale
        ? { offerAssumptions: offerAssumptions ?? defaultCheckedAssumptionTexts() }
        : {}),
      ...(warrantyType ? { warrantyType } : {}),
      ...(exclusions && exclusions.length > 0 ? { exclusions } : {}),
      ...(step3SectionIncludes ? { step3SectionIncludes } : {}),
      ...(step3Extras ? { step3Extras } : {}),
      ...(warrantyNotes !== undefined ? { warrantyNotes } : {}),
    };
  } catch {
    return null;
  }
}

/** Pusty szkic MVP — nowy draft (domyślne z preferencji firmy, nie z projektu). */
export function freshMvpBundle(): MvpBundle {
  const prefs = loadCompanyPrefs();
  const base = defaultWizardInput();
  const workType =
    prefs.preferredWorkTypes && prefs.preferredWorkTypes.length > 0
      ? prefs.preferredWorkTypes
      : base.workType;
  const wizard = normalizeWizardScopes({
    ...base,
    region: prefs.region,
    standard: prefs.standard,
    workType,
  });
  return {
    wizard,
    lines: [],
    showEstimate: false,
    currentStep: 1,
    vatMode: prefs.vatMode,
    offerAssumptions: defaultCheckedAssumptionTexts(),
    templateType: "quick",
    entryMode: "packages",
    templateId: DEFAULT_TEMPLATE_ID,
    paymentTerms: DEFAULT_PAYMENT_TEXT,
    warrantyType: DEFAULT_WARRANTY_TEXT,
    step3SectionIncludes: { ...DEFAULT_STEP3_SECTION_INCLUDES },
    step3Extras: {
      offerValidityDays: "14",
      companyAdvantages: ["", "", ""],
      cleanupOwner: "contractor",
      showPriceVariants: false,
    },
  };
}

export function formatMvpDraftLabel(bundle: MvpBundle): string {
  const t = bundle.draftLabel?.trim();
  if (t) return t;
  const w = bundle.wizard;
  const typeLabel = parseWorkTypes(w.workType)
    .map((x) => WORK_TYPE_LABELS[x])
    .join(" + ");
  const totalArea = w.rooms?.reduce((s, r) => s + r.areaM2, 0) ?? 0;
  return `Szkic · ${typeLabel} · ${totalArea} m²`;
}

// ─── Supabase CRUD ───────────────────────────────────────────────────────────

/** Lista szkiców z Supabase (najnowsze pierwsze). */
export async function listMvpDraftSummaries(
  userId: string,
  options?: ListMvpDraftOptions,
): Promise<MvpDraftSummary[]> {
  let query = supabase
    .from("drafts")
    .select("id, title, data, updated_at, project_id")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (options?.projectId === null) {
    query = query.is("project_id", null);
  } else if (typeof options?.projectId === "string") {
    query = query.eq("project_id", options.projectId);
  }

  const { data, error } = await query;

  if (error || !data) return [];

  return data.map((row) => {
    const bundle = parseMvpBundle(row.data);
    const name =
      row.title?.trim() ||
      (bundle ? formatMvpDraftLabel(bundle) : "Szkic");
    return {
      id: row.id,
      name,
      updatedAt: Date.parse(row.updated_at),
      projectId: (row as { project_id?: string | null }).project_id ?? null,
    };
  });
}

/** Najnowszy draft (do `/mvp` → resume). */
export async function getNewestDraftId(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("drafts")
    .select("id")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .single();

  if (error || !data) return null;
  return data.id;
}

/** Pobiera pełny rekord draftu z Supabase. */
export async function getMvpDraftById(id: string, userId: string): Promise<MvpDraftRecord | null> {
  const { data, error } = await supabase
    .from("drafts")
    .select("id, title, data, updated_at")
    .eq("id", id)
    .eq("user_id", userId)
    .single();

  if (error || !data) return null;

  const bundle = parseMvpBundle(data.data);
  if (!bundle) return null;

  const name =
    data.title?.trim() || formatMvpDraftLabel(bundle);

  return {
    id: data.id,
    name,
    updatedAt: Date.parse(data.updated_at),
    bundle,
  };
}

/** Tworzy nowy pusty draft w Supabase i zwraca jego id. */
export async function createMvpDraft(
  userId: string,
  templateType: TemplateType = "quick",
  entryMode: MvpEntryMode = "packages",
): Promise<{ id: string }> {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const bundle: MvpBundle = {
    ...freshMvpBundle(),
    templateType,
    entryMode,
    ...(entryMode === "manual"
      ? {
          showEstimate: true,
          currentStep: 2,
          lines: [],
        }
      : {
          showEstimate: false,
          currentStep: 1,
        }),
  };
  const title = formatMvpDraftLabel(bundle);

  const { error } = await supabase.from("drafts").insert({
    id,
    user_id: userId,
    title,
    data: bundle as unknown as Record<string, unknown>,
    created_at: now,
    updated_at: now,
  });

  if (error) throw new Error(`createMvpDraft: ${error.message}`);
  return { id };
}

/** Usuwa szkic z Supabase. */
export async function deleteMvpDraft(id: string): Promise<void> {
  const { error } = await supabase.from("drafts").delete().eq("id", id);
  if (error) throw new Error(`deleteMvpDraft: ${error.message}`);
}

// ─── Persistence hook ────────────────────────────────────────────────────────

/**
 * Debounced autosave bundle do Supabase (tabela `drafts`).
 * Podpis kompatybilny z poprzednią wersją opartą o localStorage.
 */
export function useMvpPersistence(
  bundle: MvpBundle,
  draftId: string | undefined
): {
  hydrated: boolean;
  saveStatus: MvpSaveStatus;
  lastSavedAt: number | null;
} {
  const [hydrated, setHydrated] = useState(false);
  const [saveStatus, setSaveStatus] = useState<MvpSaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bundleRef = useRef(bundle);
  bundleRef.current = bundle;
  const draftIdRef = useRef(draftId);
  draftIdRef.current = draftId;

  // Mark as hydrated after first mount (same semantics as before)
  useEffect(() => {
    setHydrated(true);
  }, []);

  // Debounced save to Supabase on every bundle change
  useEffect(() => {
    if (!hydrated || !draftIdRef.current) return;
    setSaveStatus("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const id = draftIdRef.current;
      if (!id) return;
      const now = new Date().toISOString();
      const b = bundleRef.current;
      const title = formatMvpDraftLabel(b);
      await supabase
        .from("drafts")
        .update({
          data: b as unknown as Record<string, unknown>,
          title,
          updated_at: now,
        })
        .eq("id", id);
      setLastSavedAt(Date.now());
      setSaveStatus("saved");
      timer.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [bundle, hydrated]);

  return { hydrated, saveStatus, lastSavedAt };
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function parseOfferValidityDays(value: unknown): Step3OfferExtras["offerValidityDays"] {
  return value === "7" || value === "14" || value === "30" ? value : undefined;
}

function parseCleanupOwner(value: unknown): Step3CleanupOwner | undefined {
  return value === "contractor" || value === "client" || value === "shared" ? value : undefined;
}

function parseStringList(value: unknown, limit: number): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const list = value.slice(0, limit).map((item) => (typeof item === "string" ? item : ""));
  return list.length > 0 ? list : undefined;
}

function parseScheduleStages(value: unknown): Step3ScheduleStage[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const stages = value.slice(0, 6).flatMap((item, index): Step3ScheduleStage[] => {
    if (!item || typeof item !== "object") return [];
    const raw = item as Record<string, unknown>;
    return [
      {
        id: typeof raw.id === "string" && raw.id.trim() ? raw.id : `stage-${index}`,
        name: stringOrUndefined(raw.name) ?? "",
        duration: stringOrUndefined(raw.duration) ?? "",
      },
    ];
  });
  return stages.length > 0 ? stages : undefined;
}

function parsePriceVariants(value: unknown): Step3PriceVariant[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ids = ["standard", "comfort", "premium"] as const;
  const variants = value.slice(0, 3).flatMap((item, index): Step3PriceVariant[] => {
    if (!item || typeof item !== "object") return [];
    const raw = item as Record<string, unknown>;
    const id =
      raw.id === "standard" || raw.id === "comfort" || raw.id === "premium" ? raw.id : ids[index];
    const pricePln =
      typeof raw.pricePln === "number" && Number.isFinite(raw.pricePln) ? raw.pricePln : null;
    return [
      {
        id,
        name: stringOrUndefined(raw.name) ?? "",
        differences: stringOrUndefined(raw.differences) ?? "",
        materialStandard: stringOrUndefined(raw.materialStandard) ?? "",
        duration: stringOrUndefined(raw.duration) ?? "",
        pricePln,
        recommended: raw.recommended === true,
      },
    ];
  });
  return variants.length > 0 ? variants : undefined;
}

function parseProjectMilestones(value: unknown): Step3ProjectMilestone[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const milestones = value.slice(0, 8).flatMap((item, index): Step3ProjectMilestone[] => {
    if (!item || typeof item !== "object") return [];
    const raw = item as Record<string, unknown>;
    return [
      {
        id: typeof raw.id === "string" && raw.id.trim() ? raw.id : `milestone-${index}`,
        name: stringOrUndefined(raw.name) ?? "",
        dateOrWeek: stringOrUndefined(raw.dateOrWeek) ?? "",
        payment: stringOrUndefined(raw.payment) ?? "",
      },
    ];
  });
  return milestones.length > 0 ? milestones : undefined;
}

function parseStep3Extras(value: unknown): Step3OfferExtras | undefined {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Record<string, unknown>;
  const extras: Step3OfferExtras = {
    offerValidityDays: parseOfferValidityDays(raw.offerValidityDays),
    startTerm: stringOrUndefined(raw.startTerm),
    completionTime: stringOrUndefined(raw.completionTime),
    warrantyNotes: stringOrUndefined(raw.warrantyNotes),
    assumptionsText: stringOrUndefined(raw.assumptionsText),
    companyDescription: stringOrUndefined(raw.companyDescription),
    companyAdvantages: parseStringList(raw.companyAdvantages, 3),
    companyReferences: stringOrUndefined(raw.companyReferences),
    scheduleStages: parseScheduleStages(raw.scheduleStages),
    cleanupOwner: parseCleanupOwner(raw.cleanupOwner),
    afterAcceptance: stringOrUndefined(raw.afterAcceptance),
    showPriceVariants: raw.showPriceVariants === true,
    priceVariants: parsePriceVariants(raw.priceVariants),
    projectMilestones: parseProjectMilestones(raw.projectMilestones),
    changeRequestRules: stringOrUndefined(raw.changeRequestRules),
    communicationStandard: stringOrUndefined(raw.communicationStandard),
    partyResponsibilities: stringOrUndefined(raw.partyResponsibilities),
  };
  return Object.values(extras).some((item) => item !== undefined && item !== false) ? extras : undefined;
}
